import { type Coordinates, LngLatBounds, type LngLatBoundsLike, type LngLatLike, type MapGeoJSONFeature } from "maplibre-gl";

import type { AppSearchParams } from "@/routes/App";

// FIXME: would be required for solution to #11631
// export const queryWfsLayerJson = async (wfsRootUrl: string, typeName: string, params: Record<string, string>) => {
// 	const searchParams = new URLSearchParams({
// 		service: "WFS",
// 		version: "2.0.0",
// 		request: "GetFeature",
// 		outputFormat: "application/json",
// 		typeNames: typeName,
// 		...params,
// 	});

// 	const url = `${wfsRootUrl}?${searchParams.toString()}`;

// 	const response = await fetch(url);

// 	if (response.status !== 200) {
// 		throw new Error(response.statusText);
// 	}

// 	return await response.json();
// };


export const getBounds = (feature: MapGeoJSONFeature): LngLatBounds | undefined => {
	const geometry = feature.geometry;

	if (geometry.type === "Point") {
		const point = geometry.coordinates as LngLatLike;
		return new LngLatBounds(point, point);
	} else if (geometry.type === "LineString") {
		// From: https://maplibre.org/maplibre-gl-js/docs/examples/zoomto-linestring/
		// Geographic coordinates of the LineString
		const coordinates = geometry.coordinates as Coordinates;

		// Pass the first coordinates in the LineString to `lngLatBounds` &
		// wrap each coordinate pair in `extend` to include them in the bounds
		// result. A variation of this technique could be applied to zooming
		// to the bounds of multiple Points or Polygon geometries - it just
		// requires wrapping all the coordinates with the extend method.
		const bounds = coordinates.reduce((bounds, coord) => {
			return bounds.extend(coord);
		}, new LngLatBounds(coordinates[0], coordinates[0]));
		return bounds;
	}
};

export const getCenter = (feature: MapGeoJSONFeature): LngLatLike | undefined => {
	return getBounds(feature)?.getCenter();
};

export const serializeBounds = (bounds: LngLatBounds) => bounds.toArray();

export const deserializeBounds = (bounds?: [number, number][]) => bounds && LngLatBounds.convert(bounds as LngLatBoundsLike);

export const parseGeometryLocation = (geometry: MapGeoJSONFeature["geometry"]) => {
	switch (geometry.type) {
		case "Point":
			return geometry.coordinates;
		case "MultiPoint":
			return geometry.coordinates[0]; //use first point
		case "LineString":
			return geometry.coordinates[0]; //use first point
		case "MultiLineString":
			return geometry.coordinates[0][0]; //use first point
		case "Polygon":
			return geometry.coordinates[0][0]; //use first point
		case "MultiPolygon":
			return geometry.coordinates[0][0][0]; //use first point
	}
};

export type Position = ReturnType<typeof parseGeometryLocation>;

export const parseLinestringEndpoints = (geometry: MapGeoJSONFeature["geometry"]): Position[] | undefined => {
	if (geometry.type === "LineString") {
		return [geometry.coordinates[0], geometry.coordinates[geometry.coordinates.length - 1]];
	}
	return;
};

export const parseValidPosition = (position: Position) => {
	return position && position[0] && position[1] ? position : null;
};

export const formatPointGeometry = (coordinates: NonNullable<Position>) => {
	const [lng, lat] = coordinates;
	return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
};

const getRemainder = (a: number): number => {
	return parseFloat((a - Math.floor(a)).toFixed(4));
};

const spreadNumberArr = (numArr: number[], totalSeats: number = 100, decimalNum: number = 0): number[] => {
	if (numArr.find((x) => typeof x !== "number" || x < 0)) {
		throw new Error("This is not an array of positive numbers");
	}

	if (decimalNum > 0) {
		const multi = Math.pow(10, decimalNum);
		//call the spread no decimal against multi of total seats
		const powSpread = spreadNumberArr(numArr, totalSeats * multi, 0);
		return powSpread.map((e) => e / multi);
	}

	const sum = numArr.reduce((s, i) => s + i, 0);
	//1. distribute default seats and prioritize the party with high remainder
	const seatDistribution = numArr
		.map(function (num, index) {
			const seats = (num / sum) * totalSeats;
			return {
				seats: Math.floor(seats),
				remainder: getRemainder(seats),
				index: index,
			};
		})
		.sort((a, b) => b.remainder - a.remainder);

	//2. get the total remain seats
	const takenSeats = seatDistribution.reduce((sum, current) => sum + current.seats, 0);
	const totalRemains = totalSeats - takenSeats;

	// the total remains alway smaller tnan total parties
	for (let i = 0; i < totalRemains; i++) {
		seatDistribution[i].seats++;
	}

	return seatDistribution.sort((a, b) => a.index - b.index).map((a) => a.seats);
};

export const spreadObjArr = (
	objArr: Record<string, string | number>[],
	prop: string,
	seats: number = 100,
	decimals: number = 0
) => {
	const numArr_spread = spreadNumberArr(objArr.map((x) => x[prop]) as number[], seats, decimals);

	return objArr.map((val, i) => {
		val["percentage"] = numArr_spread[i] as number;
		return val;
	});
};

export const getPaintPropName = (type: string | undefined) => {
	if (type === "line") {
		return "line-opacity";
	}
	if (type === "symbol") {
		return "icon-opacity";
	}
	if (type === "circle") {
		return "circle-opacity";
	}
	if (type === "fill") {
		return "fill-opacity";
	}
	// Note: as per: https://maplibre.org/maplibre-style-spec/layers/#heatmap-opacity
	// data-driven styling is not yet (4.5.1) supported for HeatMap Opacity/color
	// current workaround seems OK, but opacity could be an option in future
	if (type === "heatmap") {
		return "heatmap-radius";
	}
};

export const updateViewParam = (newLoc: AppSearchParams["view"]) => {
	const url = new URL(`${window.location.origin}${window.location.pathname}`);
	if (newLoc !== null) {
		url.searchParams.set("view", newLoc as string);
	}
	window.history.pushState({}, "", url);
};
